import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { STATUS_LABELS, downloadFile, exportPdf } from "@/pages/Portal";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Euro, FileStack, Clock, TrendingUp, Upload, FileText, Send, FileDown } from "lucide-react";
import { toast } from "sonner";

const FLOW = ["eingegangen", "in_pruefung", "erstellt", "fertig"];
const ROLES = ["customer", "support", "gutachter", "admin"];

function slaColor(deadline) {
  if (!deadline) return "text-muted-foreground";
  const h = (new Date(deadline).getTime() - Date.now()) / 3600000;
  if (h < 0) return "text-destructive font-semibold";
  if (h < 4) return "text-destructive font-semibold";
  if (h < 12) return "text-amber-600 font-semibold";
  return "text-emerald-600 font-semibold";
}
function slaText(deadline) {
  if (!deadline) return "—";
  const diff = new Date(deadline).getTime() - Date.now();
  if (diff < 0) return "Überfällig";
  const h = Math.floor(diff / 3600000); const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m}m`;
}

export default function Admin() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [assessment, setAssessment] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [expertise, setExpertise] = useState("alle");

  const loadApps = async () => setApps((await api.get("/applications")).data);
  const loadStats = async () => setStats((await api.get("/admin/stats")).data);
  const loadUsers = async () => { try { setUsers((await api.get("/admin/users")).data); } catch {} };
  const loadAppointments = async () => { try { setAppointments((await api.get("/appointments")).data); } catch {} };
  useEffect(() => { loadApps(); loadStats(); loadUsers(); loadAppointments(); }, []);

  const openDetail = async (a) => { setSel(a); setAssessment(a.assessment || ""); setMessages((await api.get(`/applications/${a.id}/messages`)).data); };
  const refreshSel = async (id) => { const { data } = await api.get(`/applications/${id}`); setSel(data); loadApps(); loadStats(); };

  const changeStatus = async (status) => { await api.patch(`/applications/${sel.id}/status`, { status }); toast.success("Status aktualisiert"); refreshSel(sel.id); };
  const uploadFinal = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("kind", "stellungnahme"); fd.append("file", file);
    try { await api.post(`/applications/${sel.id}/upload`, fd); toast.success("Stellungnahme hochgeladen"); refreshSel(sel.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const send = async () => { if (!msg.trim()) return; await api.post(`/applications/${sel.id}/messages`, { text: msg }); setMsg(""); setMessages((await api.get(`/applications/${sel.id}/messages`)).data); };
  const setRole = async (uid, role) => { await api.patch(`/admin/users/${uid}/role`, { role }); toast.success("Rolle aktualisiert"); loadUsers(); };
  const saveAssessment = async () => { await api.patch(`/applications/${sel.id}/assessment`, { text: assessment }); toast.success("Beurteilung gespeichert"); refreshSel(sel.id); };
  const markPaid = async () => { await api.patch(`/applications/${sel.id}/mark-paid`); toast.success("Als bezahlt markiert – 48h-Countdown gestartet"); refreshSel(sel.id); };

  const paidApps = apps.filter((a) => a.paid || a.paypal_pending);
  const branches = [...new Set(paidApps.map((a) => a.form_data?.branche).filter(Boolean))];
  const shownApps = expertise === "alle" ? paidApps : paidApps.filter((a) => (a.form_data?.branche || "") === expertise);

  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6">Admin-Dashboard</h1>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Umsatz", value: `${stats.revenue} €`, icon: Euro },
              { label: "Bezahlte Anfragen", value: stats.paid, icon: FileStack },
              { label: "Offen", value: stats.open, icon: Clock },
              { label: "Ø Bearbeitung", value: `${stats.avg_hours} h`, icon: TrendingUp },
            ].map((s, i) => (
              <Card key={i} data-testid={`stat-${i}`} className="p-5 border-border shadow-sm">
                <div className="flex items-center justify-between text-muted-foreground"><span className="text-xs font-semibold uppercase tracking-widest">{s.label}</span><s.icon size={16} /></div>
                <div className="text-3xl font-extrabold font-display mt-2">{s.value}</div>
              </Card>
            ))}
          </div>
        )}

        <Tabs defaultValue="requests">
          <TabsList>
            <TabsTrigger value="requests" data-testid="tab-requests">Anfragen</TabsTrigger>
            <TabsTrigger value="termine" data-testid="tab-termine">Termine</TabsTrigger>
            {user?.role === "admin" && <TabsTrigger value="users" data-testid="tab-users">Nutzer & Rollen</TabsTrigger>}
          </TabsList>

          <TabsContent value="requests">
            <div className="flex items-center gap-3 mt-4 mb-3">
              <span className="text-sm text-muted-foreground">Nach Fachgebiet filtern:</span>
              <Select value={expertise} onValueChange={setExpertise}>
                <SelectTrigger className="w-56" data-testid="filter-expertise"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle Fachgebiete</SelectItem>
                  {branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Card className="border-border shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Kunde</TableHead><TableHead>Fachgebiet</TableHead><TableHead>Paket</TableHead><TableHead>Status</TableHead><TableHead>SLA (48h)</TableHead><TableHead>Bezahlt</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {shownApps.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Keine Anfragen für dieses Fachgebiet.</TableCell></TableRow>}
                  {shownApps.map((a) => (
                    <TableRow key={a.id} data-testid={`row-${a.id}`} className="cursor-pointer" onClick={() => openDetail(a)}>
                      <TableCell><div className="font-medium">{a.user_name}</div><div className="text-xs text-muted-foreground">{a.user_email}</div></TableCell>
                      <TableCell className="text-sm">{a.form_data?.branche || "—"}</TableCell>
                      <TableCell className="text-sm">{a.invoice?.package || a.lookup_key}</TableCell>
                      <TableCell><Badge variant="secondary">{STATUS_LABELS[a.status]}</Badge></TableCell>
                      <TableCell className={slaColor(a.deadline_at)}>{a.status === "fertig" ? "✓ Erledigt" : slaText(a.deadline_at)}</TableCell>
                      <TableCell className="text-sm">{a.paid ? `${a.invoice?.amount} €` : <Badge className="bg-amber-500 text-white">PayPal offen</Badge>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="border-border shadow-sm mt-4">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>E-Mail</TableHead><TableHead>Rolle</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell>
                        <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                          <SelectTrigger className="w-40" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="termine">
            <Card className="border-border shadow-sm mt-4">
              <Table>
                <TableHeader><TableRow><TableHead>Termin</TableHead><TableHead>Kunde</TableHead><TableHead>Notiz</TableHead></TableRow></TableHeader>
                <TableBody>
                  {appointments.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Keine Termine gebucht.</TableCell></TableRow>}
                  {appointments.map((a) => (
                    <TableRow key={a.id} data-testid={`appt-row-${a.id}`}>
                      <TableCell className="font-medium">{new Date(a.slot).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })} Uhr</TableCell>
                      <TableCell><div>{a.user_name}</div><div className="text-xs text-muted-foreground">{a.user_email}</div></TableCell>
                      <TableCell className="text-sm">{a.note || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {sel && (
            <>
              <DialogHeader><DialogTitle>Kundenakte · {sel.user_name}</DialogTitle></DialogHeader>
              {!sel.paid && (
                <div className="border border-amber-300 bg-amber-50 rounded-md p-3 flex items-center justify-between gap-3">
                  <span className="text-sm text-amber-900">{sel.paypal_pending ? "PayPal-Zahlung angekündigt – bitte Zahlungseingang prüfen." : "Diese Anfrage ist noch nicht bezahlt."}</span>
                  <Button size="sm" data-testid="mark-paid" onClick={markPaid} className="bg-emerald-500 hover:bg-emerald-600 text-white shrink-0">Als bezahlt markieren</Button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Fragebogen</h4>
                  <div className="space-y-1 text-sm max-h-48 overflow-y-auto">
                    {Object.entries(sel.form_data || {}).map(([k, v]) => v && <div key={k}><span className="text-muted-foreground">{k}:</span> {String(v)}</div>)}
                  </div>
                  <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4 mb-2">Dokumente</h4>
                  {(sel.files || []).map((f) => (
                    <button key={f.id} onClick={() => downloadFile(sel.id, f)} className="flex items-center gap-2 text-sm text-emerald-600 hover:underline py-0.5"><FileText size={14} /> {f.filename} <span className="text-muted-foreground">({f.kind})</span></button>
                  ))}
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Status ändern</h4>
                  <Select value={sel.status} onValueChange={changeStatus}>
                    <SelectTrigger data-testid="detail-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{FLOW.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                  <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4 mb-2">Finale Stellungnahme</h4>
                  {sel.final_file ? (
                    <button onClick={() => downloadFile(sel.id, sel.final_file)} className="flex items-center gap-2 text-sm text-emerald-600 hover:underline"><FileText size={14} /> {sel.final_file.filename}</button>
                  ) : <span className="text-sm text-muted-foreground">Noch nicht hochgeladen.</span>}
                  <label className="cursor-pointer block mt-2">
                    <input type="file" data-testid="upload-final" className="hidden" onChange={(e) => uploadFinal(e.target.files[0])} />
                    <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-emerald-500 text-white hover:bg-emerald-600"><Upload size={14} /> PDF hochladen</span>
                  </label>
                  <Button size="sm" variant="outline" data-testid="admin-export-pdf" onClick={() => exportPdf(sel.id)} className="mt-2 border-[#0A192F] text-[#0A192F]">
                    <FileDown size={14} className="mr-1" /> Stellungnahme als PDF generieren
                  </Button>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Fachkundige Beurteilung (fließt ins PDF)</h4>
                <Textarea data-testid="assessment-text" value={assessment} onChange={(e) => setAssessment(e.target.value)} rows={4} placeholder="Individueller Beurteilungstext … (leer lassen = Standardtext im PDF)" />
                <Button size="sm" data-testid="assessment-save" onClick={saveAssessment} className="mt-2 bg-[#0A192F] text-white">Beurteilung speichern</Button>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Nachrichten</h4>
                <div className="max-h-40 overflow-y-auto space-y-2 mb-2">
                  {messages.map((m) => (
                    <div key={m.id} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${["admin","gutachter","support"].includes(m.sender_role) ? "ml-auto bg-[#0A192F] text-white" : "bg-muted"}`}>
                      <div className="text-[10px] opacity-70">{m.sender_name}</div>{m.text}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="admin-msg" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Antwort an den Kunden…" />
                  <Button data-testid="admin-msg-send" onClick={send} size="icon" className="bg-[#0A192F] text-white"><Send size={16} /></Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
